const normalizeText = (value) => String(value ?? "").trim();

const firstNonEmpty = (...values) => {
	for (const value of values) {
		if (Array.isArray(value)) {
			const joined = value.filter(Boolean).join(", ").trim();
			if (joined) return joined;
			continue;
		}

		const text = normalizeText(value);
		if (text) return text;
	}

	return "";
};

const normalizeDocumentItem = (item, fallbackUploadDate) => ({
	id:
		item?.id ?? item?.entityId ?? item?.documentId ?? item?.name ?? item?.title,
	fileName: firstNonEmpty(
		item?.fileName,
		item?.entityName,
		item?.name,
		item?.title,
		item?.documentName,
		item?.label,
		"Untitled Document"
	),
	status: firstNonEmpty(
		item?.loganConnectStatus,
		item?.documentStatus,
		item?.status,
		item?.connectionStatus,
		item?.assignedStatus,
		"Linked"
	),
	uploadDate: firstNonEmpty(
		item?.uploadDate,
		item?.createdDate,
		item?.statusDate,
		fallbackUploadDate
	),
});

export const getConditionIdentifier = (value) =>
	typeof value === "string" || typeof value === "number"
		? firstNonEmpty(value)
		: firstNonEmpty(
				value?.conditionId,
				value?.entityId,
				value?.id,
				value?.value,
				value?.label
			);

const getDocumentConditionIds = (document) =>
	[
		...(Array.isArray(document?.conditions) ? document.conditions : []),
		document?.conditionId,
		document?.condition_id,
		document?.assignedTo?.entityId,
		document?.assignedTo?.conditionId,
		...(Array.isArray(document?.assignedTo)
			? document.assignedTo.map((item) => item?.entityId ?? item?.conditionId)
			: []),
	]
		.map(getConditionIdentifier)
		.filter(Boolean);

const getDocumentConditionTitles = (document) =>
	[
		document?.assignedTo?.entityName,
		...(Array.isArray(document?.assignedTo)
			? document.assignedTo.map((item) => item?.entityName ?? item?.name)
			: []),
		document?.title,
		document?.fileName,
		document?.name,
	]
		.map(normalizeText)
		.filter(Boolean);

const doesDocumentMatchConditionId = (document, conditionId) => {
	if (!conditionId) return false;

	return getDocumentConditionIds(document).some(
		(documentConditionId) => String(documentConditionId) === String(conditionId)
	);
};

const doesDocumentMatchConditionTitle = (document, conditionTitle) => {
	const normalizedConditionTitle = normalizeText(conditionTitle).toLowerCase();

	if (!normalizedConditionTitle) return false;

	return getDocumentConditionTitles(document).some(
		(documentConditionTitle) =>
			String(documentConditionTitle).trim().toLowerCase() ===
			normalizedConditionTitle
	);
};

const getAttachmentIdentifier = (attachment) =>
	firstNonEmpty(
		attachment?.id,
		attachment?.entityId,
		attachment?.attachmentId,
		attachment?.documentId,
		attachment?.name,
		attachment?.title
	);

const normalizeAttachmentDocument = (
	attachment,
	parentDocument,
	fallbackUploadDate
) =>
	normalizeDocumentItem(
		{
			...attachment,
			id: getAttachmentIdentifier(attachment),
			title: firstNonEmpty(
				attachment?.fileName,
				attachment?.title,
				attachment?.entityName,
				attachment?.name,
				parentDocument?.title,
				"Untitled Document"
			),
			fileName: firstNonEmpty(
				attachment?.fileName,
				attachment?.title,
				attachment?.entityName,
				attachment?.name,
				parentDocument?.title,
				"Untitled Document"
			),
			status: firstNonEmpty(
				attachment?.loganConnectStatus,
				attachment?.documentStatus,
				attachment?.status,
				parentDocument?.loganConnectStatus,
				parentDocument?.documentStatus,
				parentDocument?.status,
				"Linked"
			),
			uploadDate: firstNonEmpty(
				attachment?.uploadDate,
				attachment?.createdDate,
				attachment?.statusDate,
				parentDocument?.lastAttachmentDate,
				parentDocument?.uploadDate,
				parentDocument?.statusDate,
				parentDocument?.updatedDate,
				fallbackUploadDate
			),
			raw: attachment,
			parentDocument,
		},
		fallbackUploadDate
	);

const dedupeDocuments = (documents) => {
	const seen = new Map();

	documents.forEach((document) => {
		const key = String(
			document?.id ??
				document?.entityId ??
				document?.documentId ??
				document?.fileName ??
				document?.uploadDate ??
				""
		);

		if (!key) return;
		seen.set(key, document);
	});

	return Array.from(seen.values());
};

export const getRowDocumentEntityIds = (row) => {
	if (Array.isArray(row?.documents)) {
		return row.documents
			.map((document) => String(document?.entityId ?? document?.id ?? "").trim())
			.filter(Boolean);
	}

	if (Array.isArray(row?.documents?.entityIds)) {
		return row.documents.entityIds
			.map((entityId) => String(entityId).trim())
			.filter(Boolean);
	}

	if (row?.documents?.entityIds === undefined || row?.documents?.entityIds === null) {
		return [];
	}

	const normalizedEntityId = String(row.documents.entityIds).trim();
	return normalizedEntityId ? [normalizedEntityId] : [];
};

const getLoanAttachmentEntityId = (document) =>
	String(
		document?.assignedTo?.entityId ??
			document?.assignedTo?.id ??
			document?.assignedTo?.entityID ??
			document?.entityId ??
			document?.id ??
			document?.documentId ??
			document?.attachmentId ??
			""
	).trim();

const getDirectSupportingDocuments = (row) =>
	Array.isArray(row?.supportingDocuments)
		? row.supportingDocuments
		: Array.isArray(row?.linkedDocuments)
			? row.linkedDocuments
			: Array.isArray(row?.documents)
				? row.documents
				: Array.isArray(row?.attachments)
					? row.attachments
					: [];

const getEnhancedSupportingDocuments = ({
	row,
	conditionId,
	conditionTitle,
	fallbackUploadDate,
	loanAttachmentDocuments,
}) => {
	const directSupportingDocuments = getDirectSupportingDocuments(row);
	const normalizedDirectDocuments = dedupeDocuments(
		directSupportingDocuments
			.map((document) =>
				normalizeAttachmentDocument(
					document,
					row,
					fallbackUploadDate || row?.createdDate || ""
				)
			)
			.filter(Boolean)
	);
	const hasFetchedLoanAttachmentDocuments = Array.isArray(loanAttachmentDocuments)
		? loanAttachmentDocuments.length > 0
		: false;

	if (hasFetchedLoanAttachmentDocuments) {
		const resolvedDocuments = [];

		(loanAttachmentDocuments || []).forEach((document) => {
			const documentMatches =
				doesDocumentMatchConditionTitle(document, conditionTitle) ||
				doesDocumentMatchConditionId(document, conditionId);
			const attachments = Array.isArray(document?.attachments)
				? document.attachments
				: [];

			if (documentMatches) {
				resolvedDocuments.push(
					normalizeAttachmentDocument(
						document,
						document,
						fallbackUploadDate || row?.createdDate || ""
					)
				);
			}

			attachments.forEach((attachment) => {
				const attachmentMatches =
					documentMatches ||
					doesDocumentMatchConditionTitle(attachment, conditionTitle) ||
					doesDocumentMatchConditionId(attachment, conditionId);

				if (!attachmentMatches) {
					return;
				}

				resolvedDocuments.push(
					normalizeAttachmentDocument(
						attachment,
						document,
						fallbackUploadDate || row?.createdDate || ""
					)
				);
			});
		});

		return dedupeDocuments([...normalizedDirectDocuments, ...resolvedDocuments]);
	}

	return normalizedDirectDocuments;
};

const getStandardSupportingDocuments = ({
	row,
	fallbackUploadDate,
	loanAttachmentDocuments,
	rowDocumentEntityIds,
}) => {
	const matchingDocuments = (loanAttachmentDocuments || []).filter((document) => {
		const documentId = getLoanAttachmentEntityId(document);

		return (
			documentId &&
			rowDocumentEntityIds.some((entityId) => String(entityId) === documentId)
		);
	});

	return dedupeDocuments(
		matchingDocuments
			.map((document) =>
				normalizeDocumentItem(document, fallbackUploadDate || row?.createdDate || "")
			)
			.filter(Boolean)
	);
};

export const getSupportingDocumentsFromLoan = ({
	isEnhancedEnabled,
	row,
	conditionId,
	conditionTitle,
	fallbackUploadDate,
	loanAttachmentDocuments,
	rowDocumentEntityIds,
}) =>
	isEnhancedEnabled
		? getEnhancedSupportingDocuments({
				row,
				conditionId,
				conditionTitle,
				fallbackUploadDate,
				loanAttachmentDocuments,
			})
		: getStandardSupportingDocuments({
				row,
				fallbackUploadDate,
				loanAttachmentDocuments,
				rowDocumentEntityIds,
			});

const extractLoanDocumentsFromResponse = (response) =>
	Array.isArray(response?.data)
		? response.data
		: Array.isArray(response?.response)
			? response.response
			: Array.isArray(response)
				? response
				: Array.isArray(response?.data?.data)
					? response.data.data
					: Array.isArray(response?.response?.data)
						? response.response.data
						: [];

export const fetchLoanAttachmentDocuments = async ({
	loanId,
	getAttachments,
}) => {
	if (!loanId) {
		return [];
	}

	const formData = new FormData();
	formData.append("loan_guid", loanId);

	const response = await getAttachments({
		formData,
	}).unwrap();

	return extractLoanDocumentsFromResponse(response);
};

export const buildSupportingDocumentCounts = ({
	rows = [],
	isEnhancedEnabled,
	loanAttachmentDocuments = [],
}) => {
	const counts = {};

	(rows || []).forEach((row) => {
		const conditionId = getConditionIdentifier(row);
		const conditionTitle = firstNonEmpty(row?.title, row?.conditionTitle, row?.label);

		if (!conditionId) {
			return;
		}

		const rowDocumentEntityIds = getRowDocumentEntityIds(row);
		const fallbackUploadDate =
			firstNonEmpty(
				row?.lastAttachmentDate,
				row?.fulfilledDate,
				row?.statusDate,
				row?.createdDate,
				row?.updatedDate,
				row?.lastModifiedDate
			) || "";

		const supportingDocuments = getSupportingDocumentsFromLoan({
			isEnhancedEnabled,
			row,
			conditionId,
			conditionTitle,
			fallbackUploadDate,
			loanAttachmentDocuments,
			rowDocumentEntityIds,
		});

		counts[String(conditionId)] = supportingDocuments?.length || 0;
	});

	return counts;
};
